import { useMediaQuery } from '@mui/material';
import { FC, useRef, useState } from 'react';

import Icon from 'atoms/Icons/Icon';
import { media } from 'atoms/breakpoints/breakpoints';
import color from 'atoms/colors/colors';

import Flex from 'quarks/Flex';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import ComponentHeading from 'components/Heading/Heading';

// Scripts
import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentAccordion } from 'graphqlTypes';

interface AccordionProps extends FlexProps, ContentfulComponentAccordion {
  backgroundIsDark: boolean;
  accordionHeading?: ContentfulComponentAccordion['heading'];
}

const AccordionV1: FC<AccordionProps> = ({ accordionHeading, accordion, backgroundIsDark, ...props }) => {
  const [active, setActive] = useState(-1);

  const drawerRefs = useRef({});
  const isDesktop = useMediaQuery(media.lg);

  const handleItem = (index: number) => {
    setActive(index === active ? -1 : index);

    !isDesktop &&
      setTimeout(() => {
        drawerRefs.current[index].current.scrollIntoView({ behavior: 'smooth' });
      }, 0);
  };

  return (
    <Flex flexDirection="column" gap="56px" justifyContent="space-between" {...props} data-testid="accordion-v1">
      {accordionHeading && (
        <ComponentHeading backgroundIsDark={backgroundIsDark} {...accordionHeading} data-testid="accordion-heading" />
      )}
      {accordion && (
        <Flex
          flexDirection="column"
          gap="16px"
          width="100%"
          maxWidth="770px"
          marginX="auto"
          data-testid="accordion-items"
        >
          {accordion.map((item, idx: number) => (
            <Flex
              key={`${item?.heading} - ${item?.id}`}
              flexDirection="column"
              paddingAll={24}
              borderRadius="12px"
              cursor="pointer"
              css={`
                ${!isDesktop ? 'scroll-margin-top: 100px;' : ''}
              `}
              backgroundColor="gray-50"
              onClick={() => handleItem(idx)}
              ref={(drawerRefs.current[idx] ??= { current: null })}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Text
                  fontSize="textMd"
                  lineHeight="textMd"
                  fontWeight="semiBold"
                  width="100%"
                  maxWidth="80%"
                  textColor="gray-900"
                  sm={{ fontSize: 'textLg', lineHeight: 'textLg' }}
                >
                  {item?.heading}
                </Text>
                <Flex
                  transition="transform 100ms ease-in-out"
                  transform={active === idx ? 'rotate(180deg)' : 'inherit'}
                >
                  <Icon id="chevron-down" fill={color.green[300]} size={20} />
                </Flex>
              </Flex>
              <Flex
                className="accordion-body"
                flexDirection="column"
                marginTop={active === idx ? 16 : 0}
                fontSize="textMd"
                height={active === idx ? '100%' : '0'}
                maxHeight={active === idx ? 'fit-content' : '0'}
                overflowY="hidden"
                transition="all 150ms cubic-bezier(0.215, 0.610, 0.355, 1.000)"
                textColor="gray-700"
                gap="16px"
                css={`
                  li {
                    &::before {
                      top: 8px;
                    }
                  }
                `}
              >
                {item?.body && richTextParser(item.body)}
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default AccordionV1;
